import React, { useState, useEffect, useCallback } from "react";
import EditorTable from "../component/EditorTable";
import { Button, Cascader, ConfigProvider, Form, Select, Spin, Tooltip, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import enUS from "antd/locale/en_US";
import {
  getWellCompletionStringDict,
  getWellCompletionFracDict,
  getWellCompletionFracChemDict,
  deleteWellCompletionFrac,
  deleteWellCompletionFracChem,
  deleteWellCompletionString,
  getWellCompletionFrac,
  getWellCompletionFracChem,
  getWellCompletionFracChemInputConfig,
  getWellCompletionString,
  updateWellCompletionFrac,
  updateWellCompletionFracChem,
  updateWellCompletionString,
  refreshWellCompletionProjection,
} from "service/wellCompletion";
import FreshnessBadge from "components/common/FreshnessBadge";
import { getAssets } from "service/assets";
import { getWellSummary, getSimpleWellSummaries } from "service/wellSummary";
import useLoading from "components/common/useLoading";
import { titleCase } from "utils/dataUtil";
import { min, set } from "lodash";

// 物化刷新端点就绪开关：backend 落地 POST /api/completion/refresh 后置 true。
// 未就绪时"立即刷新本井"按钮降级为禁用 + tooltip"待后端"。
const PROJECTION_REFRESH_READY = true;

function CompletionEditor(props) {
  const [uwi, setUwi] = useState(null);
  const [wellName, setWellName] = useState(null);
  // const [assets, setAssets] = useState(null);
  const [assetWells, setAssetWells] = useState([]);
  const [selectedAssetWells, setSelectedAssetWells] = useState(null);
  const [defaultAssetWell] = useState(null);
  const [completionStringColumns, setCompletionStringColumns] = useState([]);
  const [completionFracturingColumns, setFracturingColumns] = useState([]);
  const [completionChemicalUsageColumns, setChemicalUsageColumns] = useState([]);

  const [LoadingIcon, showLoadingIcon, hideLoadingIcon] = useLoading(
    <Spin size="large" />,
    50
  );

  const [hasNewCompletion, setHasNewCompletion] = useState(false);
  const [chemInputConfig, setChemInputConfig] = useState(null);

  // 物化读层新鲜度（FreshnessBadge 驱动）。端点就绪前用 mock，端点返回后回填。
  const [projectionRefreshedAt, setProjectionRefreshedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefreshProjection = () => {
    if (!uwi) return;
    setRefreshing(true);
    refreshWellCompletionProjection(
      uwi,
      (data) => {
        setRefreshing(false);
        // 约定响应含 refreshed_at；缺省则用本地时间兜底回填。
        setProjectionRefreshedAt(data?.refreshed_at || new Date().toISOString());
        message.success("本井物化读层已刷新");
      },
      () => setRefreshing(false)
    );
  };

  const completionColumns = [
    {
      title: "Well Name",
      dataIndex: "well_name",
      key: "well_name",
      width: "85px",
      hideInTable: true,
    },
    {
      title: "UWI",
      dataIndex: "uwi",
      key: "uwi",
      width: "85px",
      hideInTable: true,
    },
  ];

  const fracturingColumns = [
    {
      title: "Well Name",
      dataIndex: "well_name",
      key: "well_name",
      hideInTable: true,
      hideInForm: false,
    },
    {
      title: "UWI",
      dataIndex: "uwi",
      key: "uwi",
      hideInTable: true,
      hideInForm: false,
    },
    // {
    //   title: "Job Date",
    //   dataIndex: "job_date",
    //   key: "job_date",
    //   // render: (text, record, index) => {
    //   //     let date = renderUnit(text, record, index, 'job_date');
    //   //     if (React.isValidElement(date)) return date;
    //   //     else return date.split("T")[0];
    //   // },
    // },
  ];

  const chemicalUsageColumns = [
    {
      title: "Row ID",
      dataIndex: "import_row_id",
      key: "import_row_id",
      hideInTable: true,
    },
    {
      title: "Well Name",
      dataIndex: "well_name",
      key: "well_name",
      hideInTable: true,
    },
    {
      title: "UWI",
      dataIndex: "uwi",
      key: "uwi",
      hideInTable: true,
    },
    // {
    //   title: "Stage Number",
    //   dataIndex: "stage_id",
    //   key: "stage_id",
    // },
    // {
    //   title: "Additive Id",
    //   dataIndex: "additive_id",
    //   key: "additive_id",
    // },
    // {
    //   title: "Additive Name",
    //   dataIndex: "additive_name",
    //   key: "additive_name",
    // },
    // {
    //   title: "Additive Mass",
    //   dataIndex: "additive_mass",
    //   key: "additive_mass",
    // },
    // {
    //   title: "Mass Unit",
    //   dataIndex: "mass_unit",
    //   key: "mass_unit",
    //   // defaultValue: 29,
    //   // valueType: "select",
    //   // valueEnum: () => {
    //   //   let result = {};
    //   //   chemInputConfig.map(
    //   //     (config) =>
    //   //       (result[config.id] = {
    //   //         text: config.unit,
    //   //         // selected: config.id === 29,
    //   //       })
    //   //   );
    //   //   return result;
    //   // },
    //   // render: (text, record, index) =>
    //   //   renderChemUnit(text, record, index, "additive_mass_unit_id"),
    // },
    // {
    //   title: "CAS Number",
    //   dataIndex: "cas_number",
    //   key: "cas_number",
    // },
    // {
    //   title: "Concentration",
    //   dataIndex: "concentration",
    //   key: "concentration",
    // },
    // {
    //   title: "Concentration Unit",
    //   dataIndex: "concentration_unit",
    //   key: "concentration_unit",
    //   // defaultUnitId: 32,
    //   // defaultValue: 32,
    //   // valueType: "select",
    //   // valueEnum: () => {
    //   //   let result = {};
    //   //   chemInputConfig.map(
    //   //     (config) =>
    //   //       (result[config.id] = {
    //   //         text: config.unit,
    //   //         selected: config.id === 32,
    //   //       })
    //   //   );

    //   //   return result;
    //   // },
    //   // render: (text, record, index) =>
    //   //   renderChemUnit(text, record, index, "concentration_unit_id"),
    // },
    // {
    //   title: "Purpose",
    //   dataIndex: "purpose",
    //   key: "purpose",
    // },
  ];

  const renderChemUnit = (text, record, index, fieldName) => {
    const item = record[fieldName];

    if (!item || !chemInputConfig) return item;

    return chemInputConfig.find(({ id }) => id === item)?.unit;
  };

  const onChange = (value) => {
    if (!value || value.length < 1) return;
    setSelectedAssetWells(value);
    if (value.length < 2) return;
    setUwi(value[1]);
  };

  // Load Assets and Wells list
  useEffect(() => {
    getAssets(
      (result) => {
        // setAssets(result);

        let tempAssetWells = result.map((asset) => {
          return {
            value: asset.id,
            label: asset.asset_name,
            isLeaf: false,
            // children: [],
          };
        });
        setAssetWells(tempAssetWells);
      },
      (err) => {
        // hideLoadingIcon && hideLoadingIcon();
      }
    );
  }, []);

  // Load Assets and Wells list
  const loadAssetWells = (selectedOptions) => {
    const targetOption = selectedOptions[selectedOptions.length - 1];
    if (targetOption.children && targetOption.children.length > 0) {
      return;
    }
    targetOption.loading = true;

    // getWellSummary(
    getSimpleWellSummaries(
      targetOption.value,
      (result) => {
        targetOption.loading = false;
        let wells = result.map((well) => {
          return {
            value: well.uwi,
            label: well.well_name,
          };
        });
        targetOption.children = wells;
        setAssetWells([...assetWells]);
      },
      (err) => {
        console.error(err);
      }
    );
  };

  // Load chem input config
  useEffect(() => {
    getWellCompletionFracChemInputConfig(
      (result) => {
        setChemInputConfig(result.units);
      },
      (err) => console.error("Well Summary: ", err)
    );
  }, []);

  useEffect(() => {
    getWellCompletionStringDict(
      (result) => {
        // console.log("Well Completion String: ", result);
        let columnKeys = completionColumns.map((item) => item.name);
        let dictData = result.filter(
          (item) =>
            item.element !== "uwi" &&
            item.element !== "well_name" &&
            !columnKeys.includes(item.element)
        );
        const columns = dictData.map((item) => {
          let name = null;
          if (item.element_fullname) {
            name = item.element_fullname;
          } else {
            name = titleCase(item.element);
          }
          return {
            title: name,
            dataIndex: item.element,
            key: item.element,
            // width: "max-content",
            width: 120,
            // ellipsis: { showTitle: false},
            // minWidth: "100px",
          };
        });
        completionColumns.splice(2, 0, ...columns);
        console.log("Completion Columns: ", completionColumns);
        setCompletionStringColumns(completionColumns);
      },
      (err) => console.error("Well Completion String: ", err)
    );
    getWellCompletionFracDict(
      (result) => {
        // console.log("Well Completion Frac: ", result);
        let columnKeys = fracturingColumns.map((item) => item.name);
        let dictData = result.filter(
          (item) =>
            item.element !== "uwi" &&
            item.element !== "well_name" &&
            !columnKeys.includes(item.element)
        );

        const columns = dictData.map((item) => {
          let name = null;
          if (item.element_fullname) {
            name = item.element_fullname;
          } else {
            name = titleCase(item.element);
          }
          return {
            title: name,
            dataIndex: item.element,
            key: item.element,
            // width: "max-content",
            width: 120,
          };
        });
        fracturingColumns.splice(2, 0, ...columns);
        setFracturingColumns(fracturingColumns);
      },
      (err) => console.error("Well Completion Frac: ", err)
    );
    getWellCompletionFracChemDict(
      (result) => {
        // console.log("Well Completion Frac Chem: ", result);
        let columnKeys = chemicalUsageColumns.map((item) => item.name);
        let dictData = result.filter(
          (item) =>
            item.element !== "uwi" &&
            item.element !== "well_name" &&
            item.element !== "import_row_id" &&
            !columnKeys.includes(item.element)
        );

        const columns = dictData.map((item) => {
          let name = null;
          if (item.element_fullname) {
            name = item.element_fullname;
          } else {
            name = titleCase(item.element);
          }
          return {
            title: name,
            dataIndex: item.element,
            key: item.element,
            // width: "max-content",
            width: 120,
          };
        });
        chemicalUsageColumns.splice(3, 0, ...columns);
        setChemicalUsageColumns(chemicalUsageColumns);
      },
      (err) => console.error("Well Completion Frac Chem: ", err)
    );
  }, []);

  const buildQuery = useCallback(() => { return uwi; }, [uwi]);

  return (
    <div className="page-content d-flex flex-column overflow-auto">
      <LoadingIcon />
      <ConfigProvider locale={enUS} componentSize="middle">
        <div
          style={{
            marginLeft: "8px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {"Asset/Well:  "}
          <Cascader
            options={assetWells}
            value={selectedAssetWells}
            onChange={onChange}
            defaultValue={defaultAssetWell}
            loadData={loadAssetWells}
            bordered={true}
            // showSearch={true}
            // expandTrigger={'hover'}
            placeholder="Please select"
            changeOnSelect
          />
          {/* 物化读层新鲜度标记 + 立即刷新本井（编辑器仍直读写 EAV，此处仅作用于派生宽表）*/}
          {uwi && <FreshnessBadge refreshed_at={projectionRefreshedAt} />}
          <Tooltip
            title={
              PROJECTION_REFRESH_READY
                ? "重建本井的物化宽表（不影响编辑器读写 EAV）"
                : "待后端：POST /api/completion/refresh 端点未就绪"
            }
          >
            <Button
              icon={<ReloadOutlined />}
              size="small"
              loading={refreshing}
              disabled={!uwi || !PROJECTION_REFRESH_READY}
              onClick={handleRefreshProjection}
            >
              立即刷新本井
            </Button>
          </Tooltip>
        </div>
        <div className="min-h-[300]">
          <EditorTable
            title="Well String"
            columns={completionStringColumns || []}
            // hasUnitRow={false}
            showNewRow={() => hasNewCompletion}
            // uwi={uwi}
            // allowNullUwi={false}
            dataFetcher={[
              buildQuery,
              (params, onSuccess, onError) => {
                getWellCompletionString(params, onSuccess, onError);
              },
              (dataSource) => {
                setHasNewCompletion(dataSource?.completions.length < 1);
                if (dataSource == null) return null;
                const { completions } = dataSource;
                const records = Object.keys(completions).map((key, index) => {
                  let record = completions[key];
                  record.index = index;
                  record.id = index;
                  return record;
                });
                return records;
              },
            ]}
            dataSaver={[
              updateWellCompletionString,
              (data) => {
                delete data.index;
                delete data.well_name;
                console.log(data);
                return [data];
              },
              updateWellCompletionString,
              // buildUploadParameters,
              (data) => {
                delete data.index;
                delete data.well_name;
                delete data.id;
                data.uwi = uwi;
                console.log(data);
                return [data];
              }
            ]}
            dataRemover={[deleteWellCompletionString, (record) => [record.id]]}
            loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
          />
        </div>
        <div className="min-h-[300]">
          <EditorTable
            title="Well Fracturing"
            columns={completionFracturingColumns || []}
            // uwi={uwi}
            // allowNullUwi={false}
            dataFetcher={[
              buildQuery,
              getWellCompletionFrac,
              (dataSource) => {
                if (dataSource == null) return null;
                let { completions } = dataSource; // chem has no completion_units
                let wellCompletions = completions[uwi];
                if (!wellCompletions) return [];
                return wellCompletions.map((item, index) => {
                  item.index = index;
                  item.id = index;
                  item.uwi = uwi;
                  return item;
                });
              }
              // attachUnitsToDataSource(dataSource, fracturingColumns),
            ]}
            dataSaver={[
              updateWellCompletionFrac,
              (data) => {
                // [{ well_name: wellName, ...data }]
                delete data.index;
                delete data.well_name;
                console.log(data);
                return [data];
              },
              // uploadWellCompletionFrac,
              // buildUploadParameters,
              updateWellCompletionFrac,
              (data) => {
                // [{ well_name: wellName, ...data }]
                delete data.index;
                delete data.well_name;
                delete data.id;
                data.uwi = uwi;
                console.log(data);
                return [data];
              },
            ]}
            dataRemover={[deleteWellCompletionFrac, (record) => [record.id]]}
            loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
          />
        </div>
        <div className="min-h-[300]">
          <EditorTable
            title="Chemical Usage"
            columns={completionChemicalUsageColumns || []}
            //   uwi={uwi}
            //   allowNullUwi={false}
            dataFetcher={[
              buildQuery,
              getWellCompletionFracChem,
              (dataSource) => {
                if (!dataSource) return null;
                let { completions } = dataSource; // chem has no completion_units

                let currentAsset = assetWells.find(
                  (asset) => asset.value === selectedAssetWells[0]
                );
                if (!currentAsset) {
                  console.error("Can't find the asset: ", selectedAssetWells[0]);
                  return;
                }
                if (!currentAsset.children || currentAsset.children.length < 1) {
                  console.error("Asset has no wells: ", selectedAssetWells[0]);
                  return;
                }
                let currentWell = currentAsset.children.find(
                  (well) => well.value === selectedAssetWells[1]
                );
                if (!currentWell) {
                  console.error("Can't find the well: ", selectedAssetWells[1]);
                  return;
                }
                setWellName(currentWell.label);

                let wellCompletions = completions[uwi];
                if (!wellCompletions) return [];
                let records = wellCompletions.map((item, index) => {
                  item.index = index;
                  item.id = index;
                  item.uwi = uwi;
                  return item;
                });
                records
                  .sort((prev, next) => prev.id - next.id)
                  .map((item) => ({ ...item, well_name: currentWell.label }));

                return records;
              },
            ]}
            dataSaver={[
              updateWellCompletionFracChem,
              (data) => {
                delete data.id;
                delete data.index;
                delete data.well_name;
                return [{ uwi: uwi, ...data }]
              },
              updateWellCompletionFracChem,
              (data) => {
                delete data.id;
                delete data.index;
                delete data.well_name;
                return [{ uwi: uwi, ...data }];
              },
            ]}
            dataRemover={[deleteWellCompletionFracChem, (record) => [record.id]]}
            loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
          />
        </div>
      </ConfigProvider>
    </div>
  );
}

export default CompletionEditor;
